"use client";

import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { Property, SeenReservation } from "@/lib/types";

interface AppDataContextValue {
  reservations: SeenReservation[];
  properties: Property[];
  loading: boolean;
  refresh: () => Promise<void>;
}

const AppDataContext = createContext<AppDataContextValue>({
  reservations: [],
  properties: [],
  loading: true,
  refresh: async () => {},
});

export function useAppData() {
  return useContext(AppDataContext);
}

export function AppDataProvider({ children }: { children: React.ReactNode }) {
  const [reservations, setReservations] = useState<SeenReservation[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const lastFetchRef = useRef(0);

  async function fetchData() {
    const [rRes, pRes] = await Promise.all([
      fetch("/api/reservations"),
      fetch("/api/properties"),
    ]);
    const [rData, pData] = await Promise.all([rRes.json(), pRes.json()]);
    if (Array.isArray(rData)) setReservations(rData);
    if (Array.isArray(pData)) setProperties(pData);
    lastFetchRef.current = Date.now();
  }

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- async initial load; setLoading runs in the promise callback, not synchronously
    fetchData().finally(() => setLoading(false));

  }, []);

  useEffect(() => {
    function onFocus() {
      if (Date.now() - lastFetchRef.current > 30_000) {
        fetchData().catch(() => {});
      }
    }
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);

  }, []);

  async function refresh() {
    await fetchData().catch(() => {});
  }

  return (
    <AppDataContext.Provider value={{ reservations, properties, loading, refresh }}>
      {children}
    </AppDataContext.Provider>
  );
}
